import React, { useEffect, useRef, useState } from 'react';
import { Eyebrow, Bullet } from '../ui';

import screenDiscover  from '../../assets/images/screen_discover.png';
import screenFilters   from '../../assets/images/screen_filters.png';
import screenMessages  from '../../assets/images/screen_messages.jpg';
import screenMatch     from '../../assets/images/screen_match.jpg';

const featureBlocks = [
  {
    screen: screenDiscover,
    eyebrow: 'Discover',
    title: 'Browse profiles built around real people',
    desc: 'Every card shows who someone is, where they\'re from, and what they\'re into. No swiping blind.',
    bullets: ['Photo, name, age, location and occupation at a glance', 'Verified profile badges for trusted accounts', 'Like, super-like, or pass — your choice, always'],
  },
  {
    screen: screenFilters,
    eyebrow: 'Smart filters',
    title: 'Connect on your own terms',
    desc: 'Set the preferences that matter — age, body type, height, location — and we surface the people most likely to click with you.',
    bullets: ['Adjustable age range with a simple slider', 'Filter by city — all cities in Malawi supported', 'Preferences stay saved until you change them'],
  },
  {
    screen: screenMessages,
    eyebrow: 'Private messaging',
    title: 'Real conversations with your connections',
    desc: 'Once you both like each other the chat opens — just the two of you. See who\'s active, send messages, and let things flow naturally.',
    bullets: ['See all your connections and conversations in one place', 'Active status shows when they\'re online', 'Private — only mutual connections can message each other'],
  },
  {
    screen: screenMatch,
    eyebrow: 'The moment',
    title: 'When it\'s a connection ♥',
    desc: 'When two people like each other, you\'re both notified and a private chat opens. No awkwardness — just a genuine connection waiting to grow.',
    bullets: ['Instant notification when you both connect', 'Private chat opens immediately', 'Start your story from the very first message'],
  },
];

export default function Features() {
  const [activeIdx, setActiveIdx] = useState(0);
  const blockRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = blockRefs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveIdx(i); },
        { threshold: 0.55 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, []);

  return (
    <section className="bg-blush pt-16 pb-0 lg:pt-[100px] lg:pb-0 px-[5%]" id="features">
      <div className="relative max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20">

        {/* Sticky phone — desktop only */}
        <div className="hidden lg:block">
          <div className="sticky top-[120px] h-[490px]">
            <div className="w-[240px] h-[490px] bg-black rounded-[40px] p-3 shadow-[0_40px_80px_rgba(0,0,0,0.18)] overflow-hidden mx-auto">
              <div className="relative w-full h-full rounded-[28px] overflow-hidden">
                {featureBlocks.map((f, i) => (
                  <img
                    key={i}
                    src={f.screen}
                    alt={f.eyebrow}
                    className="absolute inset-0 w-full h-full object-cover rounded-[28px] block transition-opacity duration-500"
                    style={{ opacity: activeIdx === i ? 1 : 0 }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Feature blocks */}
        <div>
          {featureBlocks.map((f, i) => (
            <div
              key={i}
              ref={el => { blockRefs.current[i] = el; }}
              className={`min-h-[300px] lg:min-h-[420px] flex flex-col justify-center py-10 lg:py-[60px] border-b border-black/6 last:border-b-0 transition-all duration-400 ${activeIdx === i ? 'opacity-100 translate-y-0' : 'lg:opacity-30 lg:translate-y-5'}`}
            >
              {/* Phone mockup — mobile only, shown inline per block */}
              <div className="lg:hidden flex justify-center mb-7">
                <div className="w-[160px] h-[328px] bg-black rounded-[27px] p-[7px] shadow-[0_24px_56px_rgba(0,0,0,0.18)] overflow-hidden">
                  <img src={f.screen} alt={f.eyebrow} className="w-full h-full object-cover rounded-[20px] block" />
                </div>
              </div>

              <Eyebrow>{f.eyebrow}</Eyebrow>
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold tracking-tight mb-4 leading-snug">{f.title}</h2>
              <p className="text-base text-muted leading-relaxed mb-6">{f.desc}</p>
              <ul className="space-y-3">
                {f.bullets.map((b, j) => <Bullet key={j}>{b}</Bullet>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
